/**
 * Um texto do bloco no documento da mesa (E202.F1; E202.F2, GUS-145, ADR-0145 §5–§7): o parágrafo, o
 * item, a nota ou o texto da caixa como o papel o imprime. O texto de redação traz a marca discreta
 * "Editado" (ou "Editado na análise N"), a de rascunho não salvo e, com a edição ligada, responde ao
 * clique (e ao Enter): o editor abre no lugar (`ParteEmEdicao`). O trecho tirado fica no lugar, sem
 * texto, com "Restaurar". E203.F2 (ADR-0146 §1): o texto sem trecho de redação (o resultado, o
 * fecho, o alerta) abre o painel do dado pela origem do bloco.
 */
import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { TrechoNaTelaData } from '@/lib/correcaoDoRascunho';
import type { OrigemDoBlocoData, ParteDoBlocoData, TipoDoBloco } from '@/lib/documentoDaSaida';

import { clicavel, REALCE_CLICAVEL, REALCE_DO_DADO } from './clicavel';
import { textoDosParagrafos, useAbrirDado } from './pedidoDoDado';
import { modoDoTrecho } from './inicioDoBloco';
import { PALETA } from './paleta';
import { ParteEmEdicao } from './ParteEmEdicao';
import { ParagrafosDoDocumento } from './TrechosDoDocumento';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa;

export interface ContextoDasPartes {
  trechos: ReadonlyMap<string, TrechoNaTelaData>;
  avaliacaoId: string | null;
  /** A edição no documento; `null`: só leitura (saída assinada, ou fora da mesa). */
  edicao: EdicaoNoDocumento | null;
}

/** "Editado" quando a edição vigente muda o texto; nada no original, no tirado e no restaurado. */
export function marcaDaParte(parte: ParteDoBlocoData, contexto: ContextoDasPartes): string | null {
  if (parte.trecho_id === null) return null;
  const vigente = contexto.trechos.get(parte.trecho_id)?.vigente ?? null;
  if (vigente === null || vigente.acao === 'restaurar' || vigente.acao === 'remover') return null;
  if (contexto.avaliacaoId !== null && vigente.avaliacao_id !== contexto.avaliacaoId) {
    return t.editadoNaAnalise.replace('{rodada}', String(vigente.rodada));
  }
  return t.editado;
}

function Marcas({ marca, comRascunho }: { marca: string | null; comRascunho: boolean }) {
  if (marca === null && !comRascunho) return null;
  return (
    <p className="flex flex-wrap items-center gap-1 text-[0.72em] font-medium uppercase tracking-[0.08em]">
      {marca !== null ? (
        <span style={{ color: PALETA.navy2 }} data-testid="documento-marca-editado">
          {marca}
        </span>
      ) : null}
      {comRascunho ? (
        <Badge variant="warning" data-testid="documento-marca-rascunho">
          {t.edicao.rascunho}
        </Badge>
      ) : null}
    </p>
  );
}

function Tirada({
  trecho,
  edicao,
}: {
  trecho?: TrechoNaTelaData | undefined;
  edicao: EdicaoNoDocumento | null;
}) {
  return (
    <p
      className="my-1 flex flex-wrap items-center gap-2 border-l-2 border-dashed pl-2 text-[0.9em] italic"
      style={{ borderColor: PALETA.linha, color: PALETA.mudo }}
      data-trecho={trecho?.id}
      data-testid="documento-parte-tirada"
    >
      {t.tirado}
      {trecho !== undefined && edicao !== null ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="not-italic"
          onClick={() => edicao.abrir(trecho.id, 'restaurar')}
          data-testid="documento-restaurar"
        >
          {t.edicao.restaurar}
        </Button>
      ) : null}
    </p>
  );
}

function ParteFechada({
  parte,
  contexto,
  abrir,
  comRascunho,
  doDado = false,
}: {
  parte: ParteDoBlocoData;
  contexto: ContextoDasPartes;
  abrir: ((elemento: HTMLElement) => void) | null;
  comRascunho: boolean;
  /** E203.F2: o clique abre o painel do dado, e não o editor. */
  doDado?: boolean;
}): JSX.Element {
  const marca = marcaDaParte(parte, contexto);
  const classes = [marca !== null ? 'border-l-2 pl-2' : ''];
  if (abrir !== null) classes.push(`-mx-1 px-1 ${doDado ? REALCE_DO_DADO : REALCE_CLICAVEL}`);
  return (
    <div
      className={classes.join(' ').trim()}
      style={marca !== null ? { borderColor: PALETA.ciano } : undefined}
      {...clicavel(abrir, doDado ? ptBR.painelDoDado.dica : undefined)}
      data-trecho={parte.trecho_id ?? undefined}
      data-testid={parte.trecho_id !== null ? 'documento-parte-de-redacao' : 'documento-parte'}
    >
      <Marcas marca={marca} comRascunho={comRascunho} />
      <ParagrafosDoDocumento paragrafos={parte.paragrafos} />
    </div>
  );
}

/** A parte que não abre o editor: o texto protegido abre o painel do dado pela origem do bloco. */
function ParteSemEditor({
  parte,
  contexto,
  origem,
  secao,
}: {
  parte: ParteDoBlocoData;
  contexto: ContextoDasPartes;
  origem: OrigemDoBlocoData | null;
  secao: string | null;
}): JSX.Element {
  const abrirDado = useAbrirDado();
  const protegida = parte.trecho_id === null && origem !== null && abrirDado !== null;
  const abrir = protegida
    ? (elemento: HTMLElement) =>
        abrirDado({ origem, impresso: textoDosParagrafos(parte.paragrafos), secao, elemento })
    : null;
  return (
    <ParteFechada
      parte={parte}
      contexto={contexto}
      abrir={abrir}
      comRascunho={false}
      doDado={protegida}
    />
  );
}

export function ParteDoDocumento({
  parte,
  contexto,
  tipoDoBloco,
  origem = null,
  secao = null,
}: {
  parte: ParteDoBlocoData;
  contexto: ContextoDasPartes;
  tipoDoBloco: TipoDoBloco;
  /** E203.F2: a origem do bloco; o texto protegido abre o painel do dado por ela. */
  origem?: OrigemDoBlocoData | null;
  secao?: string | null;
}): JSX.Element {
  const trecho = parte.trecho_id !== null ? contexto.trechos.get(parte.trecho_id) : undefined;
  const edicao = trecho !== undefined ? contexto.edicao : null;
  if (trecho === undefined || edicao === null) {
    if (parte.paragrafos.length === 0) return <Tirada edicao={null} />;
    return <ParteSemEditor parte={parte} contexto={contexto} origem={origem} secao={secao} />;
  }
  const rascunho = edicao.rascunhos.get(trecho.id);
  if (edicao.aberto === trecho.id) {
    const modo = modoDoTrecho(trecho, tipoDoBloco);
    const props = { trecho, paragrafos: parte.paragrafos, modo, rascunho, edicao };
    return <ParteEmEdicao key={trecho.id} {...props} />;
  }
  if (parte.paragrafos.length === 0) return <Tirada trecho={trecho} edicao={edicao} />;
  return (
    <ParteFechada
      parte={parte}
      contexto={contexto}
      abrir={() => edicao.abrir(trecho.id)}
      comRascunho={rascunho !== undefined}
    />
  );
}
